import {
    Fragment,
    ReactNode,
    cloneElement,
    useEffect,
    useState,
    isValidElement,
    ReactElement,
} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import DismissButton from './DismissButton';
import Body from './Body';
import { cn } from '../../utils';

type ModalProps = {
    closeIcon?: boolean;
    title: string;
    message?: ReactNode[] | ReactNode;
    width?: string | number;
    height?: string | number;
    icon?: boolean | ReactNode;
    primaryAction?: ReactNode;
    secondaryAction?: ReactNode;
    children?: ReactNode;
    open?: boolean;
    onClose?: () => void;
    maskClosable?: boolean;
    className?: string;
} & AlignProp &
    VariantProp;

export type VariantProp = {
    variant?: Variant;
};

export enum Align {
    CENTER = 'center',
    LEFT = 'left',
}

export enum Variant {
    NORMAL = 'normal',
    SUCCESS = 'success',
    WARNING = 'warning',
    CAUTION = 'caution',
    ERROR = 'error',
}

export type AlignProp = {
    align?: Align;
};

export default function Modal({
    title,
    closeIcon = true,
    message,
    width,
    height,
    children,
    align = Align.LEFT,
    icon = false,
    variant,
    primaryAction,
    secondaryAction,
    open = false,
    onClose,
    maskClosable = true,
    className,
}: ModalProps) {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    if (secondaryAction && isValidElement(secondaryAction)) {
        const sClick = secondaryAction?.props.onClick;
        const secondaryClick = () => {
            setIsOpen(false);
            sClick && sClick();
        };
        secondaryAction = cloneElement(secondaryAction as ReactElement, {
            onClick: secondaryClick,
        });
    }

    const actions = [primaryAction, secondaryAction];

    return (
        <Transition.Root show={isOpen} as={Fragment} data-testid="Modal">
            <Dialog
                className="alli-relative alli-z-50"
                onClose={() => {
                    if (maskClosable) {
                        setIsOpen(false);
                        onClose?.();
                    }
                }}>
                <Transition.Child
                    as={Fragment}
                    enter="alli-ease-out alli-duration-300"
                    enterFrom="alli-opacity-0"
                    enterTo="alli-opacity-100"
                    leave="alli-ease-in alli-duration-200"
                    leaveFrom="alli-opacity-100"
                    leaveTo="alli-opacity-0">
                    <div className="alli-fixed alli-inset-0 alli-bg-black/60 alli-transition-opacity" />
                </Transition.Child>

                <div className="alli-fixed alli-inset-0 alli-z-10 alli-w-screen alli-overflow-y-auto">
                    <div className="alli-flex alli-min-h-full alli-items-end alli-justify-center alli-p-4 alli-text-center sm:alli-items-center sm:alli-p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="alli-ease-out alli-duration-300"
                            enterFrom="alli-opacity-0 alli-translate-y-4 sm:alli-translate-y-0 sm:alli-scale-95"
                            enterTo="alli-opacity-100 alli-translate-y-0 sm:alli-scale-100"
                            leave="alli-ease-in alli-duration-200"
                            leaveFrom="alli-opacity-100 alli-translate-y-0 sm:alli-scale-100"
                            leaveTo="alli-opacity-0 alli-translate-y-4 sm:alli-translate-y-0 sm:alli-scale-95">
                            <Dialog.Panel
                                className={cn(
                                    `alli-relative alli-transform alli-overflow-hidden alli-rounded-lg alli-bg-white alli-px-4 alli-pb-4 alli-pt-5 alli-text-left alli-shadow-xl alli-transition-all sm:alli-my-8 sm:alli-w-full ${!width && 'sm:alli-max-w-md'} sm:alli-p-6`,
                                    className,
                                )}
                                style={{
                                    ...(width !== undefined && {
                                        width:
                                            typeof width === 'string'
                                                ? width
                                                : `${width}px`,
                                    }),
                                    ...(height !== undefined && {
                                        height:
                                            typeof height === 'string'
                                                ? height
                                                : `${height}px`,
                                    }),
                                }}>
                                <Body
                                    title={title}
                                    message={message}
                                    align={align}
                                    icon={icon}
                                    variant={variant}
                                    children={children}
                                />
                                {actions && (
                                    <div
                                        data-testid="modal-actions"
                                        className={cn({
                                            'alli-flex alli-justify-center alli-space-x-3 sm:alli-mt-6':
                                                align === Align.CENTER,
                                            'alli-justify-beginning alli-mt-5 alli-flex alli-flex-row-reverse alli-space-x-3 alli-space-x-reverse':
                                                align === Align.LEFT,
                                        })}>
                                        {actions.length > 0 &&
                                            actions.map((action, index) => {
                                                return (
                                                    <Fragment
                                                        key={`action-${index}`}>
                                                        {action}
                                                    </Fragment>
                                                );
                                            })}
                                    </div>
                                )}

                                {closeIcon && (
                                    <div className="alli-absolute alli-right-0 alli-top-0 alli-hidden alli-pr-4 alli-pt-4 sm:alli-block">
                                        <DismissButton
                                            onClick={() => {
                                                setIsOpen(false);
                                                onClose && onClose();
                                            }}
                                        />
                                    </div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
